import jsPDF from 'jspdf';
import QRCode from 'qrcode';

/**
 * Generate and download a 1-page "Paper Wallet" PDF for a GhostComm recovery key.
 *
 * Design principles:
 *  - Print-ready on US Letter (8.5 × 11 in) — works on A4 too.
 *  - Vector text (selectable / copy-able) plus a large scannable QR.
 *  - No dependencies on backend; runs entirely client-side.
 *  - Branded but minimal — prints well on B&W laser printers.
 */
export async function generateRecoveryKeyPaperWallet({ recoveryKey, username }) {
  if (!recoveryKey) throw new Error('recoveryKey is required');

  const doc = new jsPDF({ unit: 'pt', format: 'letter' }); // 612 × 792 pt
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 48; // outer margin
  const safeName = (username || 'account').replace(/[^A-Za-z0-9._-]/g, '_');
  const generatedAt = new Date().toISOString();

  // --- Header --------------------------------------------------------------
  doc.setFillColor(15, 17, 21); // near-black
  doc.rect(0, 0, W, 64, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('Government Shield', M, 40);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Recovery Key · Paper Wallet', M, 54);

  // Right side: date
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 190);
  doc.text(`Generated: ${generatedAt}`, W - M, 54, { align: 'right' });

  // --- Account --------------------------------------------------------------
  let y = 100;
  doc.setTextColor(30, 30, 30);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Account', M, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(14);
  y += 20;
  doc.text(username || '(unknown)', M, y);

  // --- QR Code -------------------------------------------------------------
  const qrPng = await QRCode.toDataURL(recoveryKey, {
    errorCorrectionLevel: 'Q',
    margin: 1,
    width: 720, // render high-res; scale in PDF
    color: { dark: '#000000', light: '#FFFFFF' },
  });

  const QR_SIZE = 220;
  const qrX = (W - QR_SIZE) / 2;
  const qrY = y + 24;
  doc.addImage(qrPng, 'PNG', qrX, qrY, QR_SIZE, QR_SIZE);

  // Subtle caption
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text('Scan to restore', W / 2, qrY + QR_SIZE + 14, { align: 'center' });

  // --- Recovery Key text box -----------------------------------------------
  const keyY = qrY + QR_SIZE + 36;
  doc.setDrawColor(210, 150, 30); // amber accent
  doc.setLineWidth(1);
  doc.roundedRect(M, keyY, W - 2 * M, 70, 6, 6, 'S');

  doc.setTextColor(180, 110, 10);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('RECOVERY KEY', M + 12, keyY + 18);

  doc.setTextColor(20, 20, 20);
  doc.setFont('courier', 'bold');
  doc.setFontSize(13);
  // Key is already grouped as 8x8 dashed; break into two lines of 4 groups for readability
  const parts = recoveryKey.split('-');
  const line1 = parts.slice(0, 4).join('-');
  const line2 = parts.slice(4).join('-');
  doc.text(line1, W / 2, keyY + 40, { align: 'center' });
  doc.text(line2, W / 2, keyY + 60, { align: 'center' });

  // --- Warning --------------------------------------------------------------
  const warnY = keyY + 90;
  doc.setDrawColor(200, 60, 60);
  doc.setFillColor(254, 242, 242);
  doc.roundedRect(M, warnY, W - 2 * M, 56, 6, 6, 'FD');
  doc.setTextColor(160, 30, 30);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('IMPORTANT', M + 12, warnY + 16);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(60, 30, 30);
  const warnLines = [
    'Anyone holding this key can reset your password and take over your account.',
    'Store this sheet OFFLINE. Do not email it, photograph it to cloud storage, or share it.',
    'Government Shield cannot recover this key for you. If you lose it, your account is lost.',
  ];
  warnLines.forEach((t, i) => doc.text(t, M + 12, warnY + 30 + i * 10));

  // --- Notes area ----------------------------------------------------------
  const notesY = warnY + 72;
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(M, notesY, W - 2 * M, 84, 6, 6, 'S');
  doc.setTextColor(110, 110, 110);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('NOTES (optional — write on the printed copy)', M + 12, notesY + 16);
  // Ruled lines
  doc.setDrawColor(230, 230, 230);
  for (let i = 1; i <= 3; i++) {
    const ly = notesY + 16 + i * 18;
    doc.line(M + 12, ly, W - M - 12, ly);
  }

  // --- Footer --------------------------------------------------------------
  doc.setTextColor(130, 130, 140);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(
    'Government Shield · End-to-end encrypted messaging · This is a private document. Print one copy, store it somewhere safe.',
    W / 2,
    H - 24,
    { align: 'center' }
  );

  doc.save(`ghostcomm-recovery-${safeName}.pdf`);
}
